import { type GridRenderCellParams } from "@mui/x-data-grid-pro";
import { VatCode } from "src/api/generated/erp/db/types/tables/vatCode.ts";
import { VatHandlingType } from "src/api/generated/erp/db/types/tables/vatHandlingType.ts";
import i18n from "i18next";
import { TenantConfig } from "src/tenantCustomizations/TenantCustomizations.ts";
import { formatMoneyWithCurrency, getCurrencySymbolOrCode } from "src/utils/numberUtils.ts";

export function findDefaultVatHandlingType(vatHandlingTypes: VatHandlingType[]): VatHandlingType | undefined {
	return vatHandlingTypes.reduce<VatHandlingType | undefined>(
		(min, option) => (min == null || option.vatHandlingTypeId < min.vatHandlingTypeId ? option : min),
		undefined,
	);
}

export function getDefaultVatCode(vatCodes: VatCode[]): VatCode {
	const ret = vatCodes.find((vatCode) => vatCode.isDefault);
	if (ret == null) throw Error(i18n.t("default_vat_code_not_defined"));
	return ret;
}

export function calculateSalesMarginPercent({ cost, salesPrice }: { cost: number; salesPrice: number }): number {
	if (salesPrice === 0) return 0;
	return ((salesPrice - cost) / salesPrice) * 100;
}

export function getCustomerOrderTenantStateLabel(
	tenantState: string | undefined | null,
	tenantConfig: TenantConfig,
): string {
	if (tenantState === null) return "-";
	return tenantConfig.erp.customerOrderTenantStates.find((state) => state.key === tenantState)?.label ?? "-";
}

export function formatLabelWithCurrencyCode(label: string, currencyCode: string | null | undefined): string {
	const currencySymbolOrCode = getCurrencySymbolOrCode(currencyCode);
	return currencySymbolOrCode === "" ? label : `${label} (${currencySymbolOrCode})`;
}

export function renderMoneyCellWithCurrency<RowData extends object>(currencyCode: string | null | undefined) {
	return ({ value }: GridRenderCellParams<RowData, number | null | undefined>) =>
		value == null ? "" : formatMoneyWithCurrency(value, currencyCode);
}

export function renderMoneyCellWithRowCurrency<RowData extends object>(
	getCurrencyCode: (row: RowData) => string | null | undefined,
) {
	return ({ row, value }: GridRenderCellParams<RowData, number | null | undefined>) =>
		value == null ? "" : formatMoneyWithCurrency(value, getCurrencyCode(row));
}
